import * as THREE from 'three'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js'
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js'
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js'
import Stats from 'stats.js'
import * as dat from 'dat.gui'

import NOISE_GLSL from './noiseGLSL.js'
const bgColor = new THREE.Color(0x101733); // bluish purple
import { HeightFogShader, BloomTintShader} from './shaders.js'
 
class SceneManager {
  constructor() {
    this.canvas = document.querySelector('canvas.webgl')
    this.sizes = {
      width: window.innerWidth,
      height: window.innerHeight
    }

    this.init()
    this.setupHelpers()
    this.setupLights()
    this.setupPostProcessing()
    this.setupEventListeners()
  }

  init() {
    // Scene
    this.scene = new THREE.Scene()

    // Clock
    this.clock = new THREE.Clock()

    // Inject fog shader chunks that use noise
    THREE.ShaderChunk.fog_fragment = `
      #ifdef USE_FOG
      vec3 fogOrigin = cameraPosition;
      vec3 fogDirection = normalize(vWorldPosition - fogOrigin);
      float fogDepth = distance(vWorldPosition, fogOrigin);

      // f(p) = fbm( p + fbm( p ) )
      vec3 noiseSampleCoord = vWorldPosition * 0.00025 + vec3(
          0.0, 0.0, fogTime * 0.025);
          // float noiseSample = FBM(noiseSampleCoord + FBM(noiseSampleCoord)) * 0.5 + 0.5;
          float noiseSample = 0.4;
          fogDepth *= mix(noiseSample, 1.0, saturate((fogDepth - 5000.0) / 5000.0));
      fogDepth *= fogDepth;

      float heightFactor = 0.05;
      float fogFactor = heightFactor * exp(-fogOrigin.y * fogDensity) * (
          1.0 - exp(-fogDepth * fogDirection.y * fogDensity)) / fogDirection.y;
      fogFactor = saturate(fogFactor);

      gl_FragColor.rgb = mix( gl_FragColor.rgb, fogColor, fogFactor );
    #endif`;

    THREE.ShaderChunk.fog_pars_fragment = NOISE_GLSL + `
    #ifdef USE_FOG
      uniform float fogTime;
      uniform vec3 fogColor;
      varying vec3 vWorldPosition;
      #ifdef FOG_EXP2
        uniform float fogDensity;
      #else
        uniform float fogNear;
        uniform float fogFar;
      #endif
    #endif`;

    THREE.ShaderChunk.fog_vertex = `
    vec4 worldPositionv = modelMatrix * vec4(position, 1.0);
    #ifdef USE_FOG
      vWorldPosition = worldPositionv.xyz;
    #endif`;

    THREE.ShaderChunk.fog_pars_vertex = `
    #ifdef USE_FOG
      varying vec3 vWorldPosition;
    #endif`;

    function focalToFov(focalMm, sensorHeightMm = 24) {
      return 2 * Math.atan((sensorHeightMm / (2 * focalMm))) * (180 / Math.PI);
    }

    const focal = 20;
    const sensorHeight = 24;
    const fov = focalToFov(focal, sensorHeight);

    // Camera
    this.camera = new THREE.PerspectiveCamera(
      75, // default fallback
      this.sizes.width / this.sizes.height,
      0.1,
      200
    )

    this.camera.position.set(21, 11, 23.15)
    this.camera.rotation.set(0 * (Math.PI / 180),26 * (Math.PI / 180),0 * (Math.PI / 180))
    this.scene.add(this.camera)
    this.scene.background = bgColor;

    this.camera.aspect = this.sizes.width / this.sizes.height
    this.camera.updateProjectionMatrix()

    // Scene defaults (these can be overridden by app)
    this.scene.background = new THREE.Color(0x101733)
    this.scene.fog = new THREE.FogExp2(new THREE.Color(0x1b2240), 0.048)

    // Renderer
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true
    })
    this.renderer.setSize(this.sizes.width, this.sizes.height)
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2))
    this.renderer.outputEncoding = THREE.sRGBEncoding
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping
    this.renderer.toneMappingExposure = 1.2

    // optional shadow defaults (app may toggle)
    this.renderer.shadowMap.enabled = true
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap
  }

  setupHelpers() {
    // Stats
    this.stats = new Stats()
    this.stats.showPanel(0)
    document.body.appendChild(this.stats.dom)

    // GUI
    this.gui = new dat.GUI()
    this.gui.close()
  }

  setupLights() {
    const ambientLight = new THREE.AmbientLight(0xffffff, 2.5)
    this.scene.add(ambientLight)

    const pointLight = new THREE.PointLight(0xD41DFF,5);
    pointLight.position.set(
      21.50448989868164,
      13.445713996887207,
      22.247695922851562
    );
    pointLight.castShadow = true
    this.scene.add(pointLight)
  }

  setupPostProcessing() {
    this.composer = new EffectComposer(this.renderer)
    // this.composer.outputEncoding = THREE.sRGBEncoding
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;


    const renderPass = new RenderPass(this.scene, this.camera)
    this.composer.addPass(renderPass)

    // const bloomTint = new ShaderPass(BloomTintShader);
    // this.composer.addPass(bloomTint);
    // bloomTint.uniforms.mixValue.value = 0.2;

    
    //TO_DO:If emissive + bloom is still subtle, add an additive halo pass (cheap trick):
    
    // const fogPass = new ShaderPass(HeightFogShader);
    // this.composer.addPass(fogPass);
    
    // fogPass.uniforms.fogNear.value = 50;
    // fogPass.uniforms.fogFar.value = 200;
    // fogPass.uniforms.height.value = 100;      // fog mid height
    // fogPass.uniforms.heightScale.value = 0.3;
    
    // fogPass.uniforms.fogColor.value = new THREE.Color(0x101733);
    

    this.bloomPass = new UnrealBloomPass(
      new THREE.Vector2(this.sizes.width, this.sizes.height),
      0.45,
      0.5,
      0.15
    )
    
    const GrainShader = {
      uniforms: {
        tDiffuse: { value: null },
        time: { value: 0 },
        amount: { value: 0.05 }
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0);
        }
      `,
      fragmentShader: `
        uniform sampler2D tDiffuse;
        uniform float time;
        uniform float amount;
        varying vec2 vUv;

        float rand(vec2 co){
          return fract(sin(dot(co.xy ,vec2(12.9898,78.233))) * 43758.5453);
        }

        void main() {
          vec3 color = texture2D(tDiffuse, vUv).rgb;
          float noise = rand(vUv + time) * amount;
          gl_FragColor = vec4(color + noise, 1.0);
        }
      `
    };

    this.grainPass = new ShaderPass(GrainShader);
      this.composer.addPass(this.bloomPass)

    this.composer.addPass(this.grainPass);

  }

  setupEventListeners() {
    window.addEventListener('resize', () => this.onResize())
  }

  onResize() {
    this.sizes.width = window.innerWidth
    this.sizes.height = window.innerHeight

    this.camera.aspect = this.sizes.width / this.sizes.height
    this.camera.updateProjectionMatrix()

    this.renderer.setSize(this.sizes.width, this.sizes.height)
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2))

    if (this.composer) this.composer.setSize(this.sizes.width, this.sizes.height)
  }

  render() {
    if (this.composer) {
      this.composer.render()
    } else {
      this.renderer.render(this.scene, this.camera)
    }
  }

  update() {
    if (!this.clock) this.clock = new THREE.Clock()
    const delta = Math.max(0.001, Math.min(this.clock.getDelta(), 0.1))

    if (this.stats) this.stats.begin()
    // controls update would be here if used
    if (this.stats) this.stats.end()

    return delta
  }
}

export default SceneManager;
